import { Link, createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import { IconAlertTriangle, IconArrowLeft, IconBan } from "@tabler/icons-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/convex-api";

const STATE_LABELS: Record<string, string> = {
	posted: "Posted",
	negotiation: "Negotiation",
	awaiting_agreement_confirmation: "Awaiting Agreement",
	funds_staged: "Funds Staged",
	in_progress: "In Progress",
	awaiting_delivery_confirmation: "Awaiting Delivery",
	completed: "Completed",
	disputed: "Disputed",
	cancelled: "Cancelled",
};

export const Route = createFileRoute("/_authenticated/errands/$errandId")({
	component: ErrandDetailPage,
});

function formatNaira(kobo: number): string {
	return `₦${(kobo / 100).toLocaleString("en-NG", { minimumFractionDigits: 2 })}`;
}

function getStateBadge(state: string) {
	switch (state) {
		case "completed":
			return <Badge className="bg-green-500">{STATE_LABELS[state]}</Badge>;
		case "cancelled":
			return <Badge variant="secondary">{STATE_LABELS[state]}</Badge>;
		case "disputed":
			return <Badge variant="destructive">{STATE_LABELS[state]}</Badge>;
		case "in_progress":
		case "awaiting_delivery_confirmation":
			return <Badge className="bg-blue-500">{STATE_LABELS[state]}</Badge>;
		default:
			return <Badge variant="outline">{STATE_LABELS[state] ?? state}</Badge>;
	}
}

function ErrandDetailPage() {
	const { errandId } = Route.useParams();
	const data = useQuery(api.admin.getErrandDetails, {
		errandId: errandId as never,
	});
	const forceCancelErrand = useMutation(api.admin.forceCancelErrand);

	const [cancelReason, setCancelReason] = useState("");
	const [showCancelForm, setShowCancelForm] = useState(false);
	const [isCancelling, setIsCancelling] = useState(false);
	const [message, setMessage] = useState<string | null>(null);

	if (data === undefined) {
		return (
			<div className="container mx-auto space-y-6 px-4 py-10 lg:px-6">
				<Skeleton className="h-8 w-48" />
				<div className="grid gap-6 lg:grid-cols-2">
					<Skeleton className="h-64" />
					<Skeleton className="h-64" />
				</div>
			</div>
		);
	}

	if (data === null) {
		return (
			<div className="container mx-auto px-4 py-10 lg:px-6">
				<p className="text-muted-foreground">Errand not found.</p>
				<Button asChild className="mt-4" variant="outline">
					<Link to="/errands">Back to Errands</Link>
				</Button>
			</div>
		);
	}

	const {
		errand,
		customer,
		runner,
		category,
		escrow,
		negotiations,
		disputes,
		transactions,
		deliveryConfirmation,
	} = data;

	const canCancel =
		errand.state !== "completed" && errand.state !== "cancelled";

	const handleForceCancel = async () => {
		if (!cancelReason.trim()) {
			setMessage("Please provide a cancellation reason.");
			return;
		}
		setIsCancelling(true);
		setMessage(null);
		try {
			await forceCancelErrand({
				errandId: errandId as never,
				reason: cancelReason.trim(),
			});
			setMessage("Errand force-cancelled.");
			setShowCancelForm(false);
			setCancelReason("");
		} catch {
			setMessage("Failed to cancel errand.");
		} finally {
			setIsCancelling(false);
		}
	};

	return (
		<div className="container mx-auto px-4 py-10 lg:px-6">
			<div className="mb-6 flex items-center gap-4">
				<Button asChild size="sm" variant="ghost">
					<Link to="/errands">
						<IconArrowLeft className="mr-1 h-4 w-4" />
						Back
					</Link>
				</Button>
				<h1 className="max-w-lg truncate font-bold text-2xl">{errand.title}</h1>
				{getStateBadge(errand.state)}
			</div>

			<div className="grid gap-6 lg:grid-cols-2">
				{/* Errand Details */}
				<Card>
					<CardHeader>
						<CardTitle>Errand Details</CardTitle>
					</CardHeader>
					<CardContent className="space-y-3">
						<div className="text-sm">
							<span className="text-muted-foreground">Category:</span>{" "}
							{category?.displayName ?? "N/A"}
						</div>
						<div className="text-sm">
							<span className="text-muted-foreground">Task Type:</span>{" "}
							<Badge variant="outline">{errand.taskType}</Badge>
						</div>
						<div className="text-sm">
							<span className="text-muted-foreground">Location Type:</span>{" "}
							{errand.locationType}
						</div>
						<div className="text-sm">
							<span className="text-muted-foreground">Description:</span>{" "}
							{errand.fullDescription}
						</div>
						{errand.shoppingBudget != null && (
							<div className="text-sm">
								<span className="text-muted-foreground">Shopping Budget:</span>{" "}
								{formatNaira(errand.shoppingBudget)}
							</div>
						)}
					</CardContent>
				</Card>

				{/* Parties */}
				<Card>
					<CardHeader>
						<CardTitle>Parties</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4">
						<div className="rounded-md border p-3">
							<p className="mb-1 font-medium text-sm">Customer</p>
							{customer ? (
								<>
									<p className="text-sm">
										<Link
											to="/customers/$userId"
											params={{ userId: customer._id }}
											className="text-blue-600 underline-offset-2 hover:underline"
										>
											{customer.name}
										</Link>
									</p>
									<p className="text-muted-foreground text-xs">
										{customer.email}
									</p>
									<p className="text-muted-foreground text-xs">
										{customer.phoneNumber ?? "No phone"}
									</p>
								</>
							) : (
								<p className="text-muted-foreground text-sm">Unknown</p>
							)}
						</div>
						<div className="rounded-md border p-3">
							<p className="mb-1 font-medium text-sm">Runner</p>
							{runner ? (
								<>
									<p className="text-sm">
										<Link
											to="/runners/$userId"
											params={{ userId: runner._id }}
											className="text-blue-600 underline-offset-2 hover:underline"
										>
											{runner.name}
										</Link>
									</p>
									<p className="text-muted-foreground text-xs">
										{runner.email}
									</p>
									<p className="text-muted-foreground text-xs">
										{runner.phoneNumber ?? "No phone"}
									</p>
								</>
							) : (
								<p className="text-muted-foreground text-sm">
									No runner assigned
								</p>
							)}
						</div>
					</CardContent>
				</Card>

				{/* Location */}
				<Card>
					<CardHeader>
						<CardTitle>Locations</CardTitle>
					</CardHeader>
					<CardContent className="space-y-3">
						{errand.pickupAddress && (
							<div className="text-sm">
								<span className="text-muted-foreground">Pickup:</span>{" "}
								{errand.pickupAddress}
							</div>
						)}
						{errand.destinationAddress && (
							<div className="text-sm">
								<span className="text-muted-foreground">Destination:</span>{" "}
								{errand.destinationAddress}
							</div>
						)}
						{errand.stops && errand.stops.length > 0 && (
							<div className="space-y-2 pt-2">
								<p className="font-medium text-sm">Stops</p>
								{errand.stops.map(
									(stop: (typeof errand.stops)[number], idx: number) => (
										<div
											className="rounded-md border p-2 text-sm"
											key={`stop-${String(idx)}`}
										>
											<span className="font-medium">{stop.stopType}</span> —{" "}
											{stop.address ?? `(${stop.latitude}, ${stop.longitude})`}
											{stop.note && (
												<span className="text-muted-foreground">
													{" "}
													— {stop.note}
												</span>
											)}
										</div>
									),
								)}
							</div>
						)}
					</CardContent>
				</Card>

				{/* Financial Details */}
				<Card>
					<CardHeader>
						<CardTitle>Financials</CardTitle>
					</CardHeader>
					<CardContent className="space-y-3">
						{escrow ? (
							<>
								<div className="text-sm">
									<span className="text-muted-foreground">Service Fee:</span>{" "}
									{formatNaira(escrow.serviceFee)}
								</div>
								<div className="text-sm">
									<span className="text-muted-foreground">Goods Cost:</span>{" "}
									{formatNaira(escrow.goodsCost)}
								</div>
								<div className="text-sm">
									<span className="text-muted-foreground">Total Escrowed:</span>{" "}
									{formatNaira(escrow.totalAmount)}
								</div>
								{escrow.actualGoodsCost != null && (
									<div className="text-sm">
										<span className="text-muted-foreground">
											Actual Goods Cost:
										</span>{" "}
										{formatNaira(escrow.actualGoodsCost)}
									</div>
								)}
								<div className="text-sm">
									<span className="text-muted-foreground">Escrow Status:</span>{" "}
									<Badge variant="secondary">{escrow.status}</Badge>
								</div>
							</>
						) : (
							<p className="text-muted-foreground text-sm">No escrow.</p>
						)}
					</CardContent>
				</Card>

				{/* Timeline */}
				<Card className="lg:col-span-2">
					<CardHeader>
						<CardTitle>Timeline</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
							{errand.createdAt && (
								<div className="text-sm">
									<span className="block text-muted-foreground">Created</span>
									{new Date(errand.createdAt).toLocaleString()}
								</div>
							)}
							{errand.postedAt && (
								<div className="text-sm">
									<span className="block text-muted-foreground">Posted</span>
									{new Date(errand.postedAt).toLocaleString()}
								</div>
							)}
							{errand.acceptedAt && (
								<div className="text-sm">
									<span className="block text-muted-foreground">Accepted</span>
									{new Date(errand.acceptedAt).toLocaleString()}
								</div>
							)}
							{errand.fundsStagedAt && (
								<div className="text-sm">
									<span className="block text-muted-foreground">
										Funds Staged
									</span>
									{new Date(errand.fundsStagedAt).toLocaleString()}
								</div>
							)}
							{errand.startedAt && (
								<div className="text-sm">
									<span className="block text-muted-foreground">Started</span>
									{new Date(errand.startedAt).toLocaleString()}
								</div>
							)}
							{errand.completedAt && (
								<div className="text-sm">
									<span className="block text-muted-foreground">Completed</span>
									{new Date(errand.completedAt).toLocaleString()}
								</div>
							)}
							{errand.cancelledAt && (
								<div className="text-sm">
									<span className="block text-muted-foreground">Cancelled</span>
									{new Date(errand.cancelledAt).toLocaleString()}
								</div>
							)}
						</div>
						{errand.cancellationReason && (
							<div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm">
								<span className="font-medium text-red-700">
									Cancellation Reason:
								</span>{" "}
								<span className="text-red-600">
									{errand.cancellationReason}
								</span>
							</div>
						)}
						{deliveryConfirmation && (
							<div className="mt-4 rounded-md border p-3 text-sm">
								<p className="mb-1 font-medium">Delivery Confirmation</p>
								<div>
									Status:{" "}
									<Badge variant="secondary">
										{deliveryConfirmation.status}
									</Badge>
								</div>
								{deliveryConfirmation.confirmedAt && (
									<div className="mt-1 text-muted-foreground text-xs">
										Confirmed at:{" "}
										{new Date(
											deliveryConfirmation.confirmedAt,
										).toLocaleString()}
									</div>
								)}
								{deliveryConfirmation.deliveryNotes && (
									<div className="mt-1 text-muted-foreground text-xs">
										Notes: {deliveryConfirmation.deliveryNotes}
									</div>
								)}
							</div>
						)}
					</CardContent>
				</Card>
			</div>

			{/* Tabs: Negotiations, Disputes, Transactions */}
			<div className="mt-6">
				<Tabs defaultValue="negotiations">
					<TabsList>
						<TabsTrigger value="negotiations">
							Negotiations ({negotiations.length})
						</TabsTrigger>
						<TabsTrigger value="disputes">
							Disputes ({disputes.length})
						</TabsTrigger>
						<TabsTrigger value="transactions">
							Transactions ({transactions.length})
						</TabsTrigger>
					</TabsList>
					<TabsContent value="negotiations" className="mt-4">
						{negotiations.length === 0 ? (
							<p className="text-muted-foreground text-sm">No negotiations.</p>
						) : (
							<div className="rounded-md border">
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>Goods Cost</TableHead>
											<TableHead>Service Fee</TableHead>
											<TableHead>Status</TableHead>
											<TableHead>Proposed At</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{negotiations.map((neg: (typeof negotiations)[number]) => (
											<TableRow key={neg._id}>
												<TableCell>{formatNaira(neg.goodsCost)}</TableCell>
												<TableCell>{formatNaira(neg.serviceFee)}</TableCell>
												<TableCell>
													<Badge variant="secondary">{neg.status}</Badge>
												</TableCell>
												<TableCell>
													{new Date(neg.proposedAt).toLocaleString()}
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
						)}
					</TabsContent>
					<TabsContent value="disputes" className="mt-4">
						{disputes.length === 0 ? (
							<p className="text-muted-foreground text-sm">No disputes.</p>
						) : (
							<div className="rounded-md border">
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>Type</TableHead>
											<TableHead>Reason</TableHead>
											<TableHead>Status</TableHead>
											<TableHead>Created</TableHead>
											<TableHead>Actions</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{disputes.map((dispute: (typeof disputes)[number]) => (
											<TableRow key={dispute._id}>
												<TableCell>{dispute.disputeType}</TableCell>
												<TableCell className="max-w-[200px] truncate">
													{dispute.reason ?? "N/A"}
												</TableCell>
												<TableCell>
													{dispute.resolvedAt ? (
														<Badge className="bg-green-500">Resolved</Badge>
													) : (
														<Badge variant="destructive">Open</Badge>
													)}
												</TableCell>
												<TableCell>
													{new Date(dispute.createdAt).toLocaleDateString()}
												</TableCell>
												<TableCell>
													<Button asChild size="sm" variant="ghost">
														<Link
															to="/disputes/$disputeId"
															params={{ disputeId: dispute._id }}
														>
															View
														</Link>
													</Button>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
						)}
					</TabsContent>
					<TabsContent value="transactions" className="mt-4">
						{transactions.length === 0 ? (
							<p className="text-muted-foreground text-sm">No transactions.</p>
						) : (
							<div className="rounded-md border">
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>Type</TableHead>
											<TableHead>Amount</TableHead>
											<TableHead>Status</TableHead>
											<TableHead>Created</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{transactions.map((tx: (typeof transactions)[number]) => (
											<TableRow key={tx._id}>
												<TableCell>
													<Badge variant="outline">{tx.type}</Badge>
												</TableCell>
												<TableCell>{formatNaira(tx.amount)}</TableCell>
												<TableCell>
													<Badge
														variant={
															tx.status === "completed"
																? "default"
																: "secondary"
														}
													>
														{tx.status}
													</Badge>
												</TableCell>
												<TableCell>
													{new Date(tx.createdAt).toLocaleString()}
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
						)}
					</TabsContent>
				</Tabs>
			</div>

			{/* Force Cancel */}
			{canCancel && (
				<Card className="mt-6 border-red-200">
					<CardHeader>
						<CardTitle className="flex items-center gap-2 text-red-600">
							<IconAlertTriangle className="h-5 w-5" />
							Danger Zone
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4">
						{!showCancelForm ? (
							<Button
								onClick={() => setShowCancelForm(true)}
								variant="destructive"
							>
								<IconBan className="mr-2 h-4 w-4" />
								Force Cancel Errand
							</Button>
						) : (
							<div className="space-y-3">
								<p className="text-sm">
									This will cancel the errand and refund any escrowed funds.
								</p>
								<Textarea
									onChange={(e) => setCancelReason(e.target.value)}
									placeholder="Reason for cancellation..."
									value={cancelReason}
								/>
								<div className="flex gap-3">
									<Button
										disabled={isCancelling}
										onClick={handleForceCancel}
										variant="destructive"
									>
										{isCancelling ? "Cancelling..." : "Confirm Cancel"}
									</Button>
									<Button
										onClick={() => {
											setShowCancelForm(false);
											setCancelReason("");
										}}
										variant="outline"
									>
										Nevermind
									</Button>
								</div>
							</div>
						)}
						{message && (
							<p className="text-muted-foreground text-sm">{message}</p>
						)}
					</CardContent>
				</Card>
			)}
		</div>
	);
}
